import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import * as FS from '@fullstory/browser';
import Intercom, { update as IntercomUpdate } from '@intercom/messenger-js-sdk';
import { RouterOutlet } from '@angular/router';
import { UsersService } from './services/users/users.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);
  private usersService = inject(UsersService);
  private readonly langKey = 'lang' as const;
  private readonly destroy = new Subject<void>();

  title = 'app-dev-payee';

  ngOnInit(): void {
    const trackingEnabled = !environment.e2e;

    if (trackingEnabled) {
      try {
        FS.init({
          orgId: 'o-1RZVV3-na1',
          devMode: !environment.production,
        });
      } catch (error) {
        console.warn('FullStory init failed', error);
      }
    }

    this.usersService.userSubject
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (user) => {
          if (!user) {
            if (trackingEnabled) {
              try {
                FS.FullStory('setIdentity', {
                  anonymous: true,
                });
              } catch (error) {
                console.warn('FullStory setIdentity failed', error);
              }
              Intercom({
                app_id: 'n2gtpl1c',
              });
            }
            window.dataLayer.push({
              user_id: null,
            });
          } else {
            if (trackingEnabled) {
              try {
                FS.FullStory('setIdentity', {
                  uid: user.id,
                  properties: {
                    email: user.email,
                    emailVerified: user.email_verified,
                  },
                });
              } catch (error) {
                console.warn('FullStory setIdentity failed', error);
              }
              Intercom({
                app_id: 'n2gtpl1c',
                user_id: user.id,
                user_hash: user.intercom_hash,
                name: user.name!,
                email: user.email,
                created_at: new Date(user.created_at).valueOf(),
              });
            }
            window.dataLayer.push({
              user_id: user.id,
            });
          }
        },
      });

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy))
      .subscribe((lang) => {
        document.querySelector('html')?.setAttribute('lang', lang.lang);
        if (trackingEnabled) {
          IntercomUpdate({
            language_override: lang.lang,
          });
        }
      });

    this.translate.addLangs(['hu', 'en', 'sk']);
    this.translate.setDefaultLang('en');

    const browserLang = this.translate.getBrowserLang();
    const savedLang = localStorage.getItem(this.langKey);

    const usedLang = savedLang ?? browserLang ?? 'en';
    this.translate.use(
      this.translate.langs.includes(usedLang) ? usedLang : 'en',
    );
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }
}
